import {
  Box, Checkbox, FormControl, IconButton, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, TextField, Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useEffect, useState } from "react";
import { DashboardFilters, Preset, addDays, localDate } from "./filters";
import { Meta } from "./types";

const PRESET_LABELS: Record<Preset, string> = {
  "24h": "Last 24 hours", "7d": "Last 7 days", "30d": "Last 30 days", "90d": "Last 90 days", custom: "Custom range",
};

export default function FilterBar({ filters, tenants, maxRangeDays, onChange, onRefresh }: {
  filters: DashboardFilters;
  tenants: Meta["tenants"];
  maxRangeDays: number;
  onChange: (next: Partial<DashboardFilters>) => void;
  onRefresh: () => void;
}) {
  const [gameId, setGameId] = useState(filters.gameId);
  useEffect(() => setGameId(filters.gameId), [filters.gameId]);

  const setPreset = (preset: Preset) => {
    if (preset !== "custom") return onChange({ preset, from: "", to: "" });
    const today = localDate(new Date());
    onChange({ preset, from: addDays(today, -6), to: today });
  };
  const nameOf = (code: string) => tenants.find((t) => t.code === code)?.name ?? code;

  return (
    <Box sx={{
      display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2,
      position: "sticky", top: 0, zIndex: 2, backgroundColor: "background.default", py: 1,
    }}>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel shrink>Tenants</InputLabel>
        <Select
          multiple displayEmpty label="Tenants" value={filters.tenants}
          onChange={(e) => onChange({ tenants: typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value })}
          renderValue={(selected) => (selected.length === 0 ? "All tenants" : selected.map(nameOf).join(", "))}
          input={<OutlinedInput notched label="Tenants" />}
        >
          {tenants.map((t) => (
            <MenuItem key={t.code} value={t.code}>
              <Checkbox size="small" checked={filters.tenants.indexOf(t.code) >= 0} />
              <ListItemText primary={t.name} secondary={t.code} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 170 }}>
        <InputLabel>Range</InputLabel>
        <Select label="Range" value={filters.preset} onChange={(e) => setPreset(e.target.value as Preset)}>
          {(Object.keys(PRESET_LABELS) as Preset[]).map((p) => <MenuItem key={p} value={p}>{PRESET_LABELS[p]}</MenuItem>)}
        </Select>
      </FormControl>

      {filters.preset === "custom" && (
        <>
          <TextField
            size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.from}
            onChange={(e) => e.target.value && e.target.value <= filters.to && onChange({ from: e.target.value })}
          />
          <TextField
            size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.to}
            helperText={`At most ${maxRangeDays} days`}
            onChange={(e) => e.target.value && e.target.value >= filters.from && onChange({ to: e.target.value })}
          />
        </>
      )}

      <TextField
        size="small" label="Game id" placeholder="any" value={gameId}
        onChange={(e) => setGameId(e.target.value)}
        onBlur={() => gameId.trim() !== filters.gameId && onChange({ gameId: gameId.trim() })}
        onKeyDown={(e) => { if (e.key === "Enter") onChange({ gameId: gameId.trim() }); }}
      />

      <Tooltip title="Refresh">
        <IconButton onClick={onRefresh}><RefreshIcon /></IconButton>
      </Tooltip>
    </Box>
  );
}
